import { GameCore } from '@web-publish-id/game-sdk/core'
import { injectSecretConfig } from '@web-publish-id/shared/config'
import { signSecret } from './config'
import { GoogleProvider } from '@web-publish-id/auth-sdk/providers'
import { GameLoginCallback, GameLoginIframeHandles, GameLoginIframeOptions, GameLoginPopupHandles } from './types'
import { createDeviceId, getPageLangCode } from '@web-publish-id/shared'
import { AuthDeviceInfo } from '@web-publish-id/shared/types'
import { HttpServerError } from '@web-publish-id/shared/http'
import {
  publishAccountAuthExchange,
  publishAccountSsoAuth,
  publishAccountThirdLogin,
} from '@web-publish-id/shared/apis'
import { GameSDKAuthToken, GameSDKInitOptions } from '@web-publish-id/game-sdk/types'
import { setGameTokenInfo } from '@web-publish-id/shared/caches'
import { clearSession, getSessionId, IframeBridge, initSession } from '@web-publish-id/shared/helper'
import { createPopup } from '@web-publish-id/shared/helper'
import { createIframe } from '@web-publish-id/shared/helper'

const iframeBridge = new IframeBridge()

export class GameSDKPrivate extends GameCore {
  private googleProvider: GoogleProvider

  private loginCallback: GameLoginCallback

  // 设备信息
  public deviceData: AuthDeviceInfo

  constructor() {
    super()
    this.initDeviceData()
  }

  public async init(options: GameSDKInitOptions) {
    super.init(options)

    this.googleProvider = new GoogleProvider()
  }

  /**
   * 弹窗登录
   * @param handles 回调
   */
  public loginPopup(handles: GameLoginPopupHandles) {
    const url = this.getLoginUrl({
      // TODO: 后续改成用域名白名单，就可以不走重定向校验
      // 重写redirect_uri,用域名就行
      redirect_uri: window.location.origin,
      window_mode: 'popup',
    })

    const popupHandler = createPopup(url, {
      onMessage: (type, payload) => {
        if (type === 'login') {
          const authResult = this.handleAuthResult(payload)
          popupHandler.close()
          handles?.onResolve(authResult)
        }
      },
      onClose: () => {
        this.logger.info('loginPopup closed')
        handles?.onClose()
      },
    })
  }

  /**
   * 内嵌登录页面渲染
   * @param selector iframe容器id选择器
   * @param handles 回调
   * @param options 配置
   */
  public setupLoginPage(selector: string, handles: GameLoginIframeHandles = {}, options: GameLoginIframeOptions = {}) {
    const loginElement = document.querySelector(selector)
    if (!loginElement) {
      throw new Error('Login element not found')
    }

    const { hideSubject, skipAuth, theme, pageAlign, hideEmailLogin } = options
    const params: any = {
      // TODO: 后续改成用域名白名单，就可以不走重定向校验
      // 重写redirect_uri,用域名就行
      redirect_uri: window.location.origin,

      window_mode: 'insert',
      hide_subject: hideSubject,
      skip_auth: skipAuth,
      page_align: pageAlign,
      hide_email_login: hideEmailLogin,
      theme,
    }

    // 过滤掉没有值的参数
    Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key]
      }
    })

    const iframe = createIframe({
      url: this.getLoginUrl(params),
      appendId: selector,
    })

    iframeBridge.init(iframe, {
      onload: (payload) => {
        handles.onLoad?.()
      },
      onerror: (e) => {
        handles.onError?.(e)
      },
    })

    iframeBridge.on('login', (payload) => {
      const authResult = this.handleAuthResult(payload)
      handles.onResolve?.(authResult)
      iframeBridge.destroy()
    })

    // 监听页面高度变化
    iframeBridge.on('height', (payload) => {
      handles.onHeight?.(payload)
    })

    return {
      destroy: () => {
        iframeBridge.destroy()
      },
    }
  }

  /**
   * 初始化第三方登录
   * @param channel 渠道
   * @param clientId 客户端ID
   */
  public async initThird(channel: 'google', clientId: string) {
    this.logger.info('initThird', channel, clientId)

    if (!clientId) {
      this.logger.error('clientId is required !')
      throw new Error('clientId is required !')
    }

    if (channel === 'google') {
      return this.googleProvider.installSDK(clientId)
    }
  }

  /**
   * Google登录
   */
  public ggLogin(callback: GameLoginCallback) {
    this.loginCallback = callback

    this.googleProvider.oAuth(
      (accessToken) => this.channelAuth(2, accessToken),
      (e) => callback({ err: true }),
    )
  }

  /**
   * 渠道登录
   * @param channel_type 渠道类型
   * @param auth_token 授权token
   */
  private channelAuth(channel_type: number, auth_token: string) {
    this.logger.info('channelAuth', channel_type, auth_token)
    this.loginThird({ channel_type, auth_token })
  }

  /**
   * 第三方登录
   * @param params
   */
  public async loginThird(params: any) {
    const data = {
      device_info: this.deviceData,
      language: this.options.language || getPageLangCode(),
      scopes: this.options.scopes,
      ...params,
    }

    const resData = await publishAccountThirdLogin(data).catch((e) => {
      this.loginResult(e)
      throw e
    })

    // 设置游戏token
    setGameTokenInfo(resData)

    this.loginResult(resData)
    return resData
  }

  /**
   * 通过海艺sdk token登录
   * @param sdk_token
   */
  public async loginBySeaart(sdk_token: string) {
    // 初始化session
    if (!getSessionId()) {
      const sessionId = initSession()
      this.tracker.addCommonProperties({ session_id: sessionId })
    }

    this.tracker.trackSsoLoginStart(sdk_token)

    const resData = await publishAccountSsoAuth({
      sdk_token,
      client_id: this.options.client_id,
      scopes: this.options.scopes,
      language: this.options.language || getPageLangCode(),
      device_info: this.deviceData,
    }).catch((e) => {
      const errorMsg = e instanceof Error ? e.message : String(e)
      this.tracker.trackSsoLoginFailed(sdk_token, errorMsg)
      return this.loginResult(e)
    })

    if (resData.err) {
      clearSession()
      return resData
    }

    this.tracker.trackSsoLoginSuccess(sdk_token, resData.expiry_at, this.options.scopes)

    setGameTokenInfo(resData)

    // 清除session
    clearSession()

    return resData
  }

  /**
   * 交换access token
   * @param access_token
   */
  public async exchangeAccessToken(access_token: string, client_id: string) {
    const resData = await publishAccountAuthExchange({
      access_token,
      client_id,
      scopes: this.options.scopes,
      language: this.options.language || getPageLangCode(),
      device_info: this.deviceData,
      st_common: {
        client_id: this.options.client_id,
      },
    }).catch((e) => {
      return this.loginResult(e)
    })

    if (resData.err) {
      return resData
    }

    // 设置游戏token
    setGameTokenInfo(resData)

    return resData
  }

  /**
   * 创建登录回调
   * @param callback
   */
  private loginResult(res: any) {
    let data: any = { data: res }

    if (res instanceof Error) {
      if (res instanceof HttpServerError) {
        data = res.toJson()
      } else {
        data = { err: true, message: res.message }
      }
    }

    if (this.loginCallback) {
      this.loginCallback(data)
      this.loginCallback = null
    }

    return data
  }

  /**
   * 初始化设备数据
   */
  private async initDeviceData() {
    createDeviceId()
      .then((id) => {
        this.deviceData.ori_device_id = id
      })
      .catch(() => {})

    const data: AuthDeviceInfo = { ori_device_id: '', os: 1 }
    this.deviceData = data
  }
}
